import React, { useState } from "react";
import { MdClose } from "react-icons/md";
import {
    Select,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
} from "@chakra-ui/react";
import { isValidValue } from "utils/helperFunctions";

function SearchableSelectField(props) {
    const {
        label,
        id,
        extra,
        placeholder,
        variant,
        value,
        disabled,
        options,
        onChange,
    } = props;

    const [searchQuery, setSearchQuery] = useState(""); // Tracks input search query
    const [isFocused, setIsFocused] = useState(false); // Tracks focus state

    // Find the selected option based on the value (ID)
    const selectedOption = options.find((option) => option.value === value);
    const selectedLabel = selectedOption ? selectedOption.label : ""; // Get the label of the selected option

    // Filter options based on the search query
    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleClearSelection = () => {
        onChange({ target: { value: "" } }); // Clear the selected value
        setSearchQuery(""); // Clear the search query as well
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value); // Update search query
    };

    const handleFocus = () => {
        setIsFocused(true); // Set focus to true when the input is focused
    };

    const handleBlur = () => {
        // Delay the blur to give time for the option to be selected
        setTimeout(() => setIsFocused(false), 100); // Delay for dropdown closing
    };

    const handleOptionSelect = (option) => {
        // Update parent value with selected option value (ID)
        onChange({
            target: { value: option.value },
        });
        setSearchQuery(option.label); // Update the search query with the label
        setIsFocused(false); // Close the dropdown immediately
    };

    return (
        <div id={id} className={extra}>
            <label
                htmlFor={id}
                className={`text-sm text-navy-700 dark:text-white ml-3 font-bold`}
            >
                {label}
            </label>
            <InputGroup mt={2}>
                {/* Input to filter the options */}
                <Input
                    value={searchQuery || selectedLabel}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    variant={variant}
                    className="!h-12 flex-grow p-2 rounded-lg border border-gray-300 bg-white dark:border-white/10 text-navy-700 dark:text-white"
                />
                <Select
                    isDisabled={disabled}
                    id={id}
                    value={value}
                    onChange={onChange}
                    className="!h-12 flex-grow p-2 rounded-lg border border-gray-300 bg-white dark:border-white/10 text-navy-700 dark:text-white"
                    borderRadius="lg"
                    size="sm"
                    variant={variant}
                    display="none" // Hide select dropdown
                >
                    {/* Empty placeholder for select */}
                    <option value="" disabled>
                        {placeholder}
                    </option>
                </Select>
                {!disabled && isValidValue(value) && (
                    <InputRightElement width="2.5rem">
                        <IconButton
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            onClick={handleClearSelection}
                            className="bg-brandLinear mt-2"
                            aria-label="Clear selection"
                        />
                    </InputRightElement>
                )}
            </InputGroup>

            {/* Display filtered options based on the search query and input focus */}
            {(isFocused) && (
                <div
                    className="bg-white dark:bg-navy-700 rounded-lg shadow-lg mt-2 absolute w-[190px] z-10 border border-gray-300 dark:border-white/10"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className="p-2 text-navy-700 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-800"
                                onClick={() => handleOptionSelect(option)} // Handle option selection
                            >
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className="p-2 text-navy-700 dark:text-white">
                            No results found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default SearchableSelectField;
